import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays, LuxonFormats, MonthYearPeriod } from '@malou-io/package-utils';

import { TopKeywordSearchImpressionsData } from '#modules/statistics-seo/models/seo/keyword-search-impressions.interface';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';

interface Data {
    monthYearPeriod: MonthYearPeriod;
    topKeywordSearchImpressionsData: TopKeywordSearchImpressionsData;
}

@Injectable()
export class TopKeywordSearchImpressionsService extends AbstractCsvService<Data> {
    private readonly _statisticsStore = inject(StatisticsStore);

    protected _getData$(): Observable<Data> {
        const monthYearPeriodFilter = this._statisticsStore.monthYearPeriodFilter();
        return of({
            monthYearPeriod: monthYearPeriodFilter,
            topKeywordSearchImpressionsData: this._statisticsStore.topKeywordSearchImpressionsData(),
        });
    }

    protected override _isDataValid(data: Data): boolean {
        return !!data && (!!data.topKeywordSearchImpressionsData.branding || !!data.topKeywordSearchImpressionsData.discovery);
    }

    protected override _getCsvHeaderRow(): string[] {
        return ['Start date', 'End date', 'User search', 'Type', 'Apparitions'];
    }

    protected override _getCsvDataRows({ monthYearPeriod, topKeywordSearchImpressionsData }: Data): CsvAsStringArrays {
        const startDate = DateTime.fromObject(monthYearPeriod.startMonthYear).startOf('month').toFormat(LuxonFormats.numericYearMonthDay);
        const endDate = DateTime.fromObject(monthYearPeriod.endMonthYear).endOf('month').toFormat(LuxonFormats.numericYearMonthDay);

        const mergedImpressions = [...topKeywordSearchImpressionsData.branding, ...topKeywordSearchImpressionsData.discovery];

        const impressionsSortedByValue = mergedImpressions.sort((a, b) => b.value - a.value);

        return impressionsSortedByValue.map((impression) => [
            startDate,
            endDate,
            impression.keywordSearch,
            impression.type,
            impression.value.toString(),
        ]);
    }
}
